/**
 * Iter 231 · Wave 4 · Tranche 2 — Gov CSR portal.
 * Inside /gov layout, gov-user auth.
 * 4 sub-tabs: Funds · Impact · Beneficiaries · Stories.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Coins, ChartLine, Users, FileText, Plus } from "@phosphor-icons/react";

const TABS = [
  { key: "funds",         label: "Funds",        icon: Coins },
  { key: "impact",        label: "Impact",       icon: ChartLine },
  { key: "beneficiaries", label: "Beneficiaries",icon: Users },
  { key: "stories",       label: "Stories",      icon: FileText },
];

export default function GovCsr() {
  const [tab, setTab] = useState("funds");
  return (
    <div className="p-6 space-y-4" data-testid="gov-csr-root">
      <div>
        <h1 className="font-heading text-2xl">CSR · Government + Sponsor Portal</h1>
        <p className="text-sm text-[#5C5A55] mt-1">
          Fund allocation, impact reporting, beneficiary logs, story drafts.
        </p>
      </div>
      <div className="flex items-center gap-1 border-b border-[#E5E2D9] overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon; const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`gov-csr-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap ${
                active ? "border-[#0F766E] text-[#0F766E]" : "border-transparent text-[#5C5A55]"
              }`}>
              <Icon size={14} weight={active?"fill":"regular"}/>{t.label}
            </button>
          );
        })}
      </div>
      {tab === "funds"         && <Funds/>}
      {tab === "impact"        && <Impact/>}
      {tab === "beneficiaries" && <Beneficiaries/>}
      {tab === "stories"       && <Stories/>}
    </div>
  );
}

function Funds() {
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [topUpFor, setTopUpFor] = useState(null);
  const load = async () => {
    try {
      const r = await api.get("/gov/csr/funds");
      setRows(r.data.funds || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="gov-csr-funds">
      <div className="flex justify-between items-center">
        <div className="text-sm text-[#5C5A55]">{rows.length} funds</div>
        <button onClick={() => setShowAdd(true)} data-testid="fund-add"
                  className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#B45309] text-white rounded">
          <Plus size={12}/>Fund
        </button>
      </div>
      <div className="bg-white rounded-lg border p-4">
        {rows.length === 0 ? <div className="text-sm text-[#8A8780]">No CSR funds</div>
          : rows.map(f => (
            <div key={f.id} className="flex items-center justify-between py-2 border-b last:border-0 text-sm"
                 data-testid={`fund-row-${f.fund_code}`}>
              <div>
                <div className="font-medium">{f.fund_code} · {f.sponsor}</div>
                <div className="text-[11px] text-[#8A8780]">
                  {f.region || "all regions"} · balance ₹{Number(f.balance).toLocaleString("en-IN")}
                </div>
              </div>
              <button onClick={() => setTopUpFor(f)} data-testid={`fund-topup-${f.fund_code}`}
                        className="text-[11px] px-2 py-1 rounded bg-[#B45309] text-white">
                Top up
              </button>
            </div>
          ))}
      </div>
      {showAdd && <FundModal onClose={() => setShowAdd(false)}
                                   onSaved={() => { setShowAdd(false); load(); }}/>}
      {topUpFor && <TopUpModal fund={topUpFor} onClose={() => setTopUpFor(null)}
                                     onSaved={() => { setTopUpFor(null); load(); }}/>}
    </div>
  );
}
function FundModal({ onClose, onSaved }) {
  const [f, setF] = useState({ fund_code: "", sponsor: "", region: "", initial_balance: 0 });
  const submit = async () => {
    try {
      await api.post("/gov/csr/funds", { ...f, fund_code: f.fund_code.toUpperCase(),
                                              initial_balance: parseFloat(f.initial_balance) });
      toast.success("Fund created"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title="New CSR fund">
      <Input placeholder="Fund code (UPPERCASE)" value={f.fund_code} onChange={v => setF({...f, fund_code:v.toUpperCase()})} testid="fund-code"/>
      <Input placeholder="Sponsor name" value={f.sponsor} onChange={v => setF({...f, sponsor:v})} testid="fund-sponsor"/>
      <Input placeholder="Region (optional)" value={f.region} onChange={v => setF({...f, region:v})} testid="fund-region"/>
      <Input placeholder="Initial balance (₹)" value={f.initial_balance} onChange={v => setF({...f, initial_balance:v})} testid="fund-bal"/>
      <button onClick={submit} data-testid="fund-save"
                className="w-full py-2 rounded bg-[#B45309] text-white">Create fund</button>
    </Modal>
  );
}
function TopUpModal({ fund, onClose, onSaved }) {
  const [amt, setAmt] = useState(""); const [ref, setRef] = useState("");
  const submit = async () => {
    try {
      await api.post(`/gov/csr/funds/${fund.fund_code}/top-up`,
        { amount: parseFloat(amt), ref });
      toast.success("Topped up"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title={`Top up ${fund.fund_code}`}>
      <div className="text-2xl font-heading font-semibold mb-3 text-[#B45309]">
        ₹{Number(fund.balance).toLocaleString("en-IN")}
      </div>
      <Input placeholder="Amount (₹)" value={amt} onChange={setAmt} testid="tu-amt"/>
      <Input placeholder="Txn ref (UTR)" value={ref} onChange={setRef} testid="tu-ref"/>
      <button onClick={submit} data-testid="tu-save"
                className="w-full py-2 rounded bg-[#B45309] text-white">Top up</button>
    </Modal>
  );
}

function Impact() {
  const [data, setData] = useState(null);
  const [fund, setFund] = useState("");
  const [funds, setFunds] = useState([]);
  useEffect(() => {
    api.get("/gov/csr/funds").then(r => setFunds(r.data.funds || []));
  }, []);
  useEffect(() => {
    const q = fund ? `?fund_code=${fund}` : "";
    api.get(`/gov/csr/impact${q}`)
      .then(r => setData(r.data)).catch(e => toast.error(formatApiError(e)));
  }, [fund]);
  return (
    <div className="space-y-3" data-testid="gov-csr-impact">
      <select value={fund} onChange={e => setFund(e.target.value)}
                data-testid="impact-fund"
                className="border rounded px-3 py-2 text-sm bg-white">
        <option value="">All funds</option>
        {funds.map(f => <option key={f.id} value={f.fund_code}>{f.fund_code}</option>)}
      </select>
      {!data ? <div className="text-sm text-[#8A8780]">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Missions" v={data.missions} c="#0F766E"/>
            <Stat label="Completed" v={data.completed_missions} c="#166534"/>
            <Stat label="Allocated" v={`₹${Number(data.total_allocated || 0).toLocaleString("en-IN")}`} c="#B45309"/>
            <Stat label="Cost / life" v={data.cost_per_life_saved != null
                                              ? `₹${Number(data.cost_per_life_saved).toLocaleString("en-IN")}`
                                              : "—"} c="#4338CA"/>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">By mission kind</div>
            {Object.entries(data.by_mission_kind || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between py-1 text-sm">
                <span className="capitalize">{k.replace("_"," ")}</span>
                <span>{v}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Beneficiaries() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/gov/csr/beneficiaries").then(r => setRows(r.data.beneficiaries || []))
      .catch(e => toast.error(formatApiError(e)));
  }, []);
  return (
    <div className="bg-white rounded-lg border p-4" data-testid="gov-csr-beneficiaries">
      <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Beneficiaries · {rows.length}</div>
      {rows.length === 0 ? <div className="text-sm text-[#8A8780]">No allocated missions</div>
        : rows.map(b => (
          <div key={b.id} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
            <div>
              <div className="font-medium">{b.mission_no} · {b.patient_name || "unnamed"}</div>
              <div className="text-[11px] text-[#8A8780]">
                {b.mission_kind} · {b.csr_fund_ref}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm">₹{Number(b.csr_amount || 0).toLocaleString("en-IN")}</div>
              <div className="text-[11px] text-[#8A8780]">{b.status}</div>
            </div>
          </div>
        ))}
    </div>
  );
}

function Stories() {
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const load = async () => {
    try {
      const r = await api.get("/gov/csr/stories");
      setRows(r.data.stories || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="gov-csr-stories">
      <div className="flex justify-between">
        <div className="text-sm text-[#5C5A55]">{rows.length} stories</div>
        <button onClick={() => setShowAdd(true)} data-testid="story-add"
                  className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#7C3AED] text-white rounded">
          <Plus size={12}/>Draft
        </button>
      </div>
      <div className="bg-white rounded-lg border p-4">
        {rows.length === 0 ? <div className="text-sm text-[#8A8780]">No stories yet</div>
          : rows.map(s => (
            <div key={s.id} className="py-2 border-b last:border-0"
                 data-testid={`story-row-${s.id.slice(0,6)}`}>
              <div className="font-medium">{s.title}</div>
              <div className="text-[11px] text-[#8A8780]">
                {s.status} · {(s.created_at || "").slice(0,10)}
              </div>
              <pre className="text-xs text-[#5C5A55] mt-1 whitespace-pre-wrap font-body">
                {s.body_md.slice(0,180)}{s.body_md.length > 180 && "…"}
              </pre>
            </div>
          ))}
      </div>
      {showAdd && <StoryModal onClose={() => setShowAdd(false)}
                                     onSaved={() => { setShowAdd(false); load(); }}/>}
    </div>
  );
}
function StoryModal({ onClose, onSaved }) {
  const [f, setF] = useState({ title: "", body_md: "" });
  const submit = async () => {
    try {
      await api.post("/gov/csr/stories", f);
      toast.success("Story drafted"); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Modal onClose={onClose} title="New CSR story">
      <Input placeholder="Title" value={f.title} onChange={v => setF({...f, title:v})} testid="story-title"/>
      <textarea placeholder="Markdown body" value={f.body_md} onChange={e => setF({...f, body_md:e.target.value})}
                   data-testid="story-body"
                   className="w-full border rounded px-3 py-2 mb-2 text-sm h-32"/>
      <button onClick={submit} data-testid="story-save"
                className="w-full py-2 rounded bg-[#7C3AED] text-white">Save draft</button>
    </Modal>
  );
}


// helpers -----------------------------------------------------------
function Stat({ label, v, c }) {
  return (
    <div className="p-4 bg-white rounded-lg border">
      <div className="text-[22px] font-heading font-semibold" style={{color:c}}>{v}</div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mt-1">{label}</div>
    </div>
  );
}
function Input({ placeholder, value, onChange, testid }) {
  return (
    <input placeholder={placeholder} value={value || ""}
             onChange={e => onChange(e.target.value)}
             data-testid={testid}
             className="w-full border rounded px-3 py-2 mb-2 text-sm"/>
  );
}
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-5 max-h-[90vh] overflow-y-auto"
             onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg">{title}</h2>
          <button onClick={onClose} className="text-2xl leading-none">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
